import React from 'react';
import { Check, CheckCircle, Sparkle, Warning } from 'phosphor-react';
import { CatalogProduct } from '../../types/entities';
import { PELES } from '../../types/enums';
import { reais } from '../../utils/moeda';
import ActionModal from './ActionModal';
import { useUltimoValor } from '../../use/usePresence';
import LazyImage from '../ui/LazyImage';
import PrimaryButton from '../buttons/PrimaryButton';

interface ProductDetailModalProps {
  product: CatalogProduct | null;
  /** Já é o produto desta etapa: o botão vira "remover" em vez de "selecionar". */
  selected?: boolean;
  /** Casa com o tipo de pele do usuário — o mesmo selo que a gaveta mostra. */
  matchesSkin?: boolean;
  /** "Etapa 2 de 5", quando a ficha é aberta de dentro de uma etapa da rotina. */
  stepLabel?: string;
  onClose: () => void;
  onSelect?: (product: CatalogProduct) => void;
  onRemove?: () => void;
  /** Substitui o rodapé de escolher/remover — a prateleira usa editar/excluir. */
  footer?: React.ReactNode;
}

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">{titulo}</p>
      <div className="text-xs text-nude-500 dark:text-gray-400 leading-relaxed">{children}</div>
    </div>
  );
}

/**
 * Ficha completa do produto, na ordem em que se decide: o que é → do que é feito →
 * para quem serve → o que entrega → como usar → com o que combina (e com o que não).
 *
 * Serve tanto ao catálogo quanto ao produto da prateleira do usuário (que chega no
 * mesmo formato): bloco sem conteúdo simplesmente não aparece.
 */
export default function ProductDetailModal({
  product: produtoProp,
  selected = false,
  matchesSkin = false,
  stepLabel,
  onClose,
  onSelect,
  onRemove,
  footer,
}: ProductDetailModalProps) {
  // Segura a ficha na tela enquanto ela sai: o pai zera o produto no fechar.
  const product = useUltimoValor(produtoProp);

  if (!product) return null;

  const ideais = product.recommendedFor ?? [];
  const ativos = product.mainActives ?? [];

  return (
    <ActionModal isOpen={Boolean(produtoProp)} onClose={onClose} title={product.name} size="lg">
      <div className="flex flex-col gap-4 max-h-[65vh] overflow-y-auto pr-1">
        {stepLabel && (
          <p className="-mt-1 text-xs text-nude-400 dark:text-gray-500">{stepLabel}</p>
        )}

        {product.imageUrl && (
          <LazyImage
            src={product.imageUrl}
            alt={product.name}
            eager
            className="w-full h-44 rounded-2xl bg-nude-50 dark:bg-gray-700/60"
            imgClassName="w-full h-full object-contain"
          />
        )}

        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            {product.brand && (
              <p className="text-xs text-nude-400 dark:text-gray-500">{product.brand}</p>
            )}
            {ideais.length > 0 && (
              <p className="text-xs text-nude-500 dark:text-gray-400 mt-0.5">
                {ideais.map((t) => PELES[t]).join(' · ')}
              </p>
            )}
          </div>
          {product.price != null && (
            <p className="text-sm font-bold text-gray-800 dark:text-gray-100 whitespace-nowrap">
              {reais(product.price)}
              <span className="text-[10px] font-normal text-nude-400 dark:text-gray-500"> em média</span>
            </p>
          )}
        </div>

        {product.owned && (
          <span className="inline-flex w-fit items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-lavender-50 dark:bg-lavender-900/20 text-lavender-600 dark:text-lavender-400">
            <Sparkle className="w-3 h-3" />
            Você já tem este produto
          </span>
        )}

        {matchesSkin && !product.owned && (
          <span className="inline-flex w-fit items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-sage-50 dark:bg-sage-900/20 text-sage-700 dark:text-sage-400">
            <CheckCircle className="w-3 h-3" />
            Indicado para a sua pele
          </span>
        )}

        {product.categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {product.categories.map((c) => (
              <span
                key={c}
                className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-nude-50 dark:bg-gray-700 text-nude-600 dark:text-gray-400 border border-nude-100 dark:border-gray-600"
              >
                {c}
              </span>
            ))}
          </div>
        )}

        {/* Duas colunas no tablet, empilhado no celular: o par lê como um bloco só. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {product.description && <Bloco titulo="Sobre o produto">{product.description}</Bloco>}
          {ativos.length > 0 && (
            <Bloco titulo="Ativos principais">
              <div className="flex flex-wrap gap-1.5 mt-1">
                {ativos.map((a) => (
                  <span
                    key={a}
                    className="px-2 py-1 rounded-full text-[11px] font-medium bg-nude-50 dark:bg-gray-700 text-nude-600 dark:text-gray-300 border border-nude-100 dark:border-gray-600"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </Bloco>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {ideais.length > 0 && (
            <Bloco titulo="Ideal para">
              <ul className="flex flex-col gap-1 mt-1">
                {ideais.map((t) => (
                  <li key={t} className="flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-sage-500 flex-shrink-0" />
                    {PELES[t]}
                  </li>
                ))}
              </ul>
            </Bloco>
          )}
          {product.benefits && <Bloco titulo="Benefícios para você">{product.benefits}</Bloco>}
        </div>

        {product.howToUse && <Bloco titulo="Como usar">{product.howToUse}</Bloco>}

        {product.combinesWith && <Bloco titulo="Combina com">{product.combinesWith}</Bloco>}

        {product.avoidCombiningWith && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
            <Warning className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">Evite combinar com</p>
              <p className="text-xs text-amber-600 dark:text-amber-400 leading-relaxed mt-0.5">
                {product.avoidCombiningWith}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4">
        {footer ?? (selected ? (
          <button
            onClick={onRemove}
            className="w-full py-3.5 rounded-button text-sm font-semibold text-nude-500 dark:text-gray-400 border-2 border-nude-200 dark:border-gray-600 hover:border-red-200 hover:text-red-500 transition-colors"
          >
            Remover desta etapa
          </button>
        ) : (
          onSelect && (
            <PrimaryButton fullWidth onClick={() => onSelect(product)}>
              Selecionar produto
            </PrimaryButton>
          )
        ))}
      </div>
    </ActionModal>
  );
}
